import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';

export interface FinalizationCardProps {
    startDate: string | null;
    allUploaded: boolean;
    submitting: boolean;
    totalDocs?: number;
    onSubmit: (endDate: string) => void | Promise<unknown>;
    translate: (key: string, options?: Record<string, unknown>) => string;
}

function todayIsoDate(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function FinalizationCard({ startDate, allUploaded, submitting, totalDocs = 3, onSubmit, translate }: FinalizationCardProps) {
    const [endDate, setEndDate] = useState<string>(todayIsoDate);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        setError(null);
        if (!endDate) {
            setError(translate('documents.endDateRequired'));
            return;
        }
        const startDateOnly = startDate ? startDate.slice(0, 10) : null;
        if (startDateOnly && endDate < startDateOnly) {
            setError(translate('documents.endDateBeforeStart'));
            return;
        }
        await onSubmit(endDate);
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {translate('documents.finalization')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {allUploaded
                        ? translate('documents.allDocsUploaded', { total: totalDocs })
                        : translate('documents.uploadAllBeforeFinish', { total: totalDocs })}
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-start' }}>
                    <TextField
                        label={translate('documents.endDateLabel')}
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={!allUploaded || submitting}
                        error={error !== null}
                        helperText={error}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                    <Button variant="contained" sx={{ color: 'white' }} disabled={!allUploaded || submitting} onClick={handleSubmit}>
                        {submitting ? translate('documents.registering') : translate('documents.registerDevEnd')}
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
}
